/**
 * gradeUtils.js
 * * Utility functions for determining student grade stages.
 * Fixed to properly identify Middle Stage and Preparatory grades, and
 * return the correct API service.
 */

// Import the API services directly to avoid circular dependencies
import apiService from '../api/apiService';
import preparatoryApiService from '../api/preparatoryApiService';
import middleApiService from '../api/middleApiService';

/**
 * Get the student's grade from various possible sources
 * @param {Object} student - The student object (child or selectedChild)
 * @param {Object} assignment - Optional assignment object which might contain grade info
 * @returns {number|null} - The grade level or null if not found
 */
export const detectGrade = (student, assignment = null) => {
    // Try to get grade from assignment course name first
    if (assignment?.course_name) {
        const gradeMatch = assignment.course_name.match(/Grade (\d+)/i);
        if (gradeMatch && gradeMatch[1]) {
            const grade = parseInt(gradeMatch[1], 10);
            console.log(`Detected grade ${grade} from assignment course name: ${assignment.course_name}`);
            return grade;
        }
    }

    // Try direct grade property on student
    if (student?.grade) {
        const grade = typeof student.grade === 'string' ? parseInt(student.grade, 10) : student.grade;
        if (!isNaN(grade)) {
            console.log(`Detected grade ${grade} from direct student grade property`);
            return grade;
        }
    }

    // Try from classes array
    if (student?.classes && Array.isArray(student.classes) && student.classes.length > 0) {
        if (student.classes[0].grade) {
            const grade = typeof student.classes[0].grade === 'string'
                ? parseInt(student.classes[0].grade, 10)
                : student.classes[0].grade;

            if (!isNaN(grade)) {
                console.log(`Detected grade ${grade} from student's first class`);
                return grade;
            }
        }

        // Try class name if it contains grade info
        if (student.classes[0].name) {
            const gradeMatch = student.classes[0].name.match(/Grade (\d+)/i);
            if (gradeMatch && gradeMatch[1]) {
                const grade = parseInt(gradeMatch[1], 10);
                console.log(`Detected grade ${grade} from class name: ${student.classes[0].name}`);
                return grade;
            }
        }
    }

    // Try class_name property
    if (student?.class_name) {
        const gradeMatch = student.class_name.match(/Grade (\d+)/i);
        if (gradeMatch && gradeMatch[1]) {
            const grade = parseInt(gradeMatch[1], 10);
            console.log(`Detected grade ${grade} from student's class name: ${student.class_name}`);
            return grade;
        }
    }

    // If no grade found, log a warning and return null
    console.warn("Could not detect grade from student or assignment");
    return null;
};

/**
 * Determine if a grade is in the preparatory stage (grades 3-5).
 * @param {number|string|null} grade - The grade level.
 * @returns {boolean} - True if preparatory stage, false otherwise.
 */
export const isPreparatoryGrade = (grade) => {
    if (grade === null || grade === undefined) return false;

    const gradeNum = typeof grade === 'string' ? parseInt(grade, 10) : grade;
    if (isNaN(gradeNum)) {
        console.warn("Invalid grade:", grade);
        return false;
    }

    // Preparatory stage is grades 3-5.
    return gradeNum >= 3 && gradeNum <= 5;
};

/**
 * Determine if a grade is in the middle stage (grades 6-8).
 * @param {number|string|null} grade - The grade level.
 * @returns {boolean} - True if middle stage, false otherwise.
 */
export const isMiddleStageGrade = (grade) => {
    if (grade === null || grade === undefined) return false;

    const gradeNum = typeof grade === 'string' ? parseInt(grade, 10) : grade;
    if (isNaN(gradeNum)) {
        console.warn("Invalid grade:", grade);
        return false;
    }

    // Middle stage is grades 6-8.
    return gradeNum >= 6 && gradeNum <= 8;
};

/**
 * Get the appropriate API service based on grade level.
 * @param {number|string|null} grade - The grade level or student object.
 * @param {Object} [student] - Optional student object if grade is not provided.
 * @param {Object} [assignment] - Optional assignment object which might contain grade info.
 * @returns {Object} - The appropriate API service for the grade.
 */
export const getApiServiceForGrade = (grade, student = null, assignment = null) => {
    // If grade is an object, assume it's a student object and try to detect grade
    let gradeValue = grade;
    if (typeof grade === 'object' && grade !== null) {
        student = grade;
        gradeValue = detectGrade(student, assignment);
    } else if (grade === null && student) {
        gradeValue = detectGrade(student, assignment);
    }

    // Determine which API service to use based on grade
    if (isMiddleStageGrade(gradeValue)) {
        console.log(`Using middle stage API service for grade ${gradeValue}`);
        return middleApiService;
    }

    if (isPreparatoryGrade(gradeValue)) {
        console.log(`Using preparatory stage API service for grade ${gradeValue}`);
        return preparatoryApiService;
    }

    console.log(`Using foundational stage API service for grade ${gradeValue || 'unknown'}`);
    return apiService;
};